import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Share, Heart, Shield, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Equipment } from '../types/equipment';
import { equipmentService } from '../services/equipmentService';
import { getImageUrl } from '../utils/imageUrl';
import { BookingSection } from '@components';

const EquipmentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [equipment, setEquipment] = useState<Equipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [totalDays, setTotalDays] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    if (id) {
      searchEquipment(id);
    }
  }, [id]);

  const searchEquipment = async (id: string) => {
    setLoading(true);
    const foundEquipment = await equipmentService.getEquipmentById(id);
    setEquipment(foundEquipment);
    setLoading(false);
  };

  useEffect(() => {
    if (startDate && endDate) {
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setTotalDays(diffDays);
    } else {
      setTotalDays(0);
    }
  }, [startDate, endDate]);

  const handleDateChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleNextImage = () => {
    if (equipment) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === equipment.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const handlePrevImage = () => {
    if (equipment) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? equipment.images.length - 1 : prevIndex - 1
      );
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center items-center min-h-[60vh]">
        <div className="animate-pulse space-y-8 w-full max-w-4xl">
          <div className="h-64 bg-gray-200 rounded-lg"></div>
          <div className="h-8 bg-gray-200 rounded w-3/4"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!equipment) {
    return (
      <div className="container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-[60vh]">
        <AlertCircle size={48} className="text-red-500 mb-4" />
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Equipo no encontrado</h1>
        <p className="text-gray-600 mb-6">El equipo que buscas no existe o ha sido eliminado.</p>
        <Link to="/search" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
          Ver otros equipos
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">{equipment.name}</h1>

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="flex items-center text-gray-600">
              <span>{equipment.description}</span>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 cursor-pointer pointer-cursor"
              onClick={() => {
                // Share functionality would go here
                alert('¡Aquí se abriría la función de compartir!');
              }}
            >
              <Share size={18} />
              <span className="hidden md:inline">Compartir</span>
            </button>
            <button
              className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 cursor-pointer pointer-cursor"
              onClick={toggleFavorite}
            >
              <Heart
                size={18}
                className={isFavorite ? "fill-red-500 text-red-500" : ""}
              />
              <span className="hidden md:inline">Guardar</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Image Gallery */}
          <div className="md:col-span-2">
            <div className="relative rounded-xl overflow-hidden bg-gray-100 h-[300px] md:h-[400px]">
              <img
                src={getImageUrl(equipment.images[currentImageIndex].url)}
                alt={equipment.name}
                className="w-full h-full object-cover object-center"
              />

              {equipment.images.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-100"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-100"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}

              {/* Image indicators */}
              {equipment.images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {equipment.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full ${currentImageIndex === index ? 'bg-white' : 'bg-white/50'
                        }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Image thumbnails */}
            {equipment.images.length > 1 && (
              <div className="flex space-x-2 mt-2 overflow-x-auto hide-scrollbar">
                {equipment.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 ${currentImageIndex === index ? 'border-blue-500' : 'border-transparent'
                      }`}
                  >
                    <img
                      src={getImageUrl(image.url)}
                      alt={`${equipment.name} thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Equipment details */}
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">Sobre este equipo</h2>
              <p className="text-gray-700 mb-6">
                {equipment.description}
              </p>

              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-lg font-semibold mb-4">Características y especificaciones</h3>
                <div className="gap-y-2">
                  {equipment.features.map((feature) => (
                    <div className="flex">
                      <div className="mr-3 text-blue-500">
                        <Shield size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Condición del equipo: {feature}</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Este equipo ha sido {feature} y está en excelentes condiciones de funcionamiento.
                        </p>
                      </div>
                    </div>
                  ))}
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                    <div className="flex">
                      <div className="mr-3 text-blue-500">
                        <Shield size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Condición del equipo</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Este equipo está en excelentes condiciones de funcionamiento.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-4">
                    <div className="flex">
                      <div className="mr-3 text-red-500">
                        <Shield size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Condición del equipo</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Este equipo está en excelentes condiciones de funcionamiento.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-lg font-semibold mb-4">¿Cómo funciona?</h3>
                <div className="bg-gray-100 rounded-lg h-[300px] flex items-center justify-center">
                  <video className="w-full h-full object-cover" controls autoPlay muted>
                    <source src="../src/assets/videoprueba.mp4" type="video/mp4" />
                    Tu navegador no soporta la etiqueta de video.
                  </video>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Section */}
          <div className="md:col-span-1">
            <BookingSection
              pricePerDay={equipment.pricePerDay}
              available={true}
              totalDays={totalDays}
              onDateChange={handleDateChange}
              equipment={{
                id: equipment.id,
                name: equipment.name,
                images: equipment.images,
              }}
              startDate={startDate}
              endDate={endDate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentPage;